/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

@file:Suppress("TooManyFunctions")

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import io.ktor.http.HttpStatusCode
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.Br
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import systems.frictionless.impulse.browser.ErrorTranslation
import systems.frictionless.impulse.browser.composable.component.CenteredOnScreen
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.InlinedErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.LoginFormContainer
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.composable.component.TextField
import systems.frictionless.impulse.browser.composable.component.Type
import systems.frictionless.impulse.browser.composable.component.WhiteBox
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.browser.util.DO_NOTHING
import systems.frictionless.impulse.model.AuthenticatedUser
import systems.frictionless.impulse.rest.client.model.Authentication
import systems.frictionless.impulse.rest.model.Email
import systems.frictionless.impulse.rest.model.Password
import systems.frictionless.impulse.viewmodel.ChangePasswordViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState

@Composable
fun ChangePasswordSection(
    changePasswordViewModel: ChangePasswordViewModel,
    onSignedIn: (AuthenticatedUser) -> Unit
) {
    /*
     * A little icky; need to retain nullable state to pass along to the next state.
     * While we tested attaching this to the states, which eliminated the null safety issues, it didn't feel right
     * from an API design standpoint.  Perhaps this should be attached to LoginViewModel instead
     *
     * In the meantime, storing the old values here, it better allows us to support a back button in the future since
     * the UI is holding onto the previous values.
     */
    val (password, setPassword) = remember { mutableStateOf<Password?>(null) }
    val (prevBearerAuth, setBearerAuth) = remember { mutableStateOf<Authentication.Bearer?>(null) }

    ContentContainer(
        navigationSection = SideNavigationSection.SignIn,
        mainContainerCssClass = AppStyleSheet.login,
        mainContentContainerId = HtmlId.PageChangePassword,
        mainContent = {
            CenteredOnScreen {
                LoginFormContainer {
                    when (val changePasswordSnapshot = changePasswordViewModel.state.collectAsState().value) {
                        is ChangePasswordViewModel.State.Form -> {
                            PasswordFormSection(changePasswordSnapshot) {
                                setPassword(it)
                                changePasswordViewModel.transition()
                            }
                        }
                        is ChangePasswordViewModel.State.SignIn ->
                            SessionSection(
                                changePasswordSnapshot,
                                changePasswordViewModel.email,
                                password!!
                            ) {
                                setBearerAuth(it)
                                changePasswordViewModel.transition()
                            }
                        is ChangePasswordViewModel.State.GetUser ->
                            GetUserSection(
                                changePasswordSnapshot,
                                prevBearerAuth!!
                            ) {
                                onSignedIn(it)
                            }
                        else -> {
                            console.log("Unexpected state", changePasswordSnapshot)
                        }
                    }
                }
            }
        }
    )
}

@Composable
private fun PasswordFormSection(
    viewModel: ChangePasswordViewModel.State.Form,
    onComplete: (Password) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> PasswordForm(null) { viewModel.startChangePassword(composableScope, it) }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure -> {
            val translation =
                ErrorTranslation.translate(state.failure) {
                    if (HttpStatusCode.Unauthorized == it.httpStatusCode) {
                        "Password reset links are only valid for a few minutes, and this link is expired.  Please submit " +
                            "a new password reset request."
                    } else {
                        "Server error occurred"
                    }
                }
            PasswordForm(translation) {
                viewModel.startChangePassword(
                    composableScope,
                    it
                )
            }
        }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result)
        }
    }
}

@Composable
private fun PasswordForm(
    errorMessage: String?,
    onSubmit: (Password) -> Unit
) {
    val (password, setPass) = remember { mutableStateOf("") }

    WhiteBox(arrayOf(AppStyleSheet.narrowBox)) {
        LoginHeader("Please enter a new password")

        val submitForm: () -> Unit = { onSubmit(Password(password)) }

        Form(action = DO_NOTHING) {
            TextField(
                value = password,
                onValueChange = setPass,
                type = Type.NewPassword,
                onEnterKey = submitForm,
                autoFocus = true,
                maxLength = Password.MAX_PASSWORD_LENGTH
            )
            errorMessage?.let {
                InlinedErrorMessage(it)
            }
            Br {}
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    if (Password.validate(password).isNotEmpty()) {
                        disabled()
                    }
                    onClick {
                        submitForm()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun LoginHeader(headerText: String) {
    H1 {
        Text(headerText)
    }
}

@Composable
private fun SessionSection(
    viewModel: ChangePasswordViewModel.State.SignIn,
    email: Email,
    password: Password,
    onComplete: (Authentication.Bearer) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            LoginProgress()
            viewModel.startSignIn(composableScope, Authentication.Form(email, password.plaintext))
        }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure -> {
            val errorMessage =
                ErrorTranslation.translate(state.failure) {
                    "A server error occurred"
                }

            SessionForm(errorMessage) {
                viewModel.startSignIn(composableScope, Authentication.Form(email, password.plaintext))
            }
        }
        is NetworkViewModelState.Complete -> {
            onComplete(state.result)
        }
    }
}

@Composable
private fun SessionForm(
    errorMessage: String,
    action: () -> Unit
) {
    WhiteBox(arrayOf(AppStyleSheet.narrowBox)) {
        InlinedErrorMessage(errorMessage)

        Div {
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    onClick {
                        action()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun GetUserSection(
    viewModel: ChangePasswordViewModel.State.GetUser,
    prevBearerAuth: Authentication.Bearer,
    onComplete: (AuthenticatedUser) -> Unit
) {
    val composableScope = rememberCoroutineScope()

    when (val state = viewModel.state.collectAsState().value) {
        is NetworkViewModelState.Idle -> {
            LoginProgress()
            viewModel.startGetUser(composableScope, prevBearerAuth)
        }
        is NetworkViewModelState.Busy -> LoginProgress()
        is NetworkViewModelState.Failure ->
            GetUserForm(state.failure.toString()) {
                viewModel.startGetUser(composableScope, prevBearerAuth)
            }
        is NetworkViewModelState.Complete -> {
            SuccessForm {
                onComplete(state.result)
            }
        }
    }
}

@Composable
private fun GetUserForm(
    errorMessage: String,
    action: () -> Unit
) {
    WhiteBox(arrayOf(AppStyleSheet.narrowBox)) {
        InlinedErrorMessage(errorMessage)

        Div {
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    onClick {
                        action()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun SuccessForm(action: () -> Unit) {
    WhiteBox(arrayOf(AppStyleSheet.narrowBox)) {
        P {
            Text("Your password was changed successfully.")
        }

        Div {
            Button(
                attrs = {
                    classes(AppStyleSheet.blueButton)
                    onClick {
                        action()
                    }
                }
            ) {
                Text("Next")
            }
        }
    }
}

@Composable
private fun LoginProgress() {
    LoadingIndicator()
}
